import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { useState, useCallback, useRef } from "react";
import { ActionsTable } from "./ActionsTable/ActionsTable";
import { ModalAddSign } from "./ModalAddSign/ModalAddSign";
import { ModalDeleteSign } from "./ModalDeleteSign/ModalDeleteSign";
import { useGetAllSigns } from "../../api/signs";
import { useAssignServicesToSigns } from "../../api/signs";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { ModalManageServices } from "./ModalManageServices/ModalManageServices";
import { ModalAssignServices } from "./ModalAssignServices/ModalAssignServices";
import { CustomTableSign } from "../../components/CustomTableSign/CustomTableSign";
import { useGetAllEtablissements } from "../../api/etablissement";
import { ModalSyncCentres } from "./ModalSyncCentres/ModalSyncCentres";
import { SyncCentresReport } from "./SyncCentresReport/SyncCentresReport";
import { SyncCentresResult } from "../../api/appointment";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  Box 
} from "@mui/material";

const columns = [
  { header: "ID", field: "id" },
  { header: "Name", field: "name" },
  { header: "Phone", field: "phone", render: (data: Record<string, any>) => data.phone || "-" },
  { header: "Email", field: "email", render: (data: Record<string, any>) => data.email || "-" },
  { header: "Address", field: "address", render: (data: Record<string, any>) => data.address?.raw || "-" },
  { 
    header: "Etablissement", 
    field: "etablissement", 
    render: (data: Record<string, any>) => data.etablissement?.name || "-" 
  },
];

type Props = {};

export const Sign: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const initSign: TSign = {
    id: -1,
    name: "",
    logo: null,
    phone: null,
    email: null,
    created_at: null,
    updated_at: "",
    picture_1: null,
    picture_2: null,
    picture_3: null,
    centre_code: null,
    postal_code: null,
    google_place_id: null,
    working_day: null,
    unworking_day: null,
    distance: 0,
    like: false,
    etablissement_id: null,
    etablissement: null,
    address: {
      raw: "",
      lat: 0,
      lng: 0,
      city: null,
      country: null
    },
    services: []
  };

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEtablissementId, setSelectedEtablissementId] = useState<number | "">(""); // New state for etablissement filter

  // Modal states
  const [openModalSign, setOpenModalSign] = useState(false);
  const [openModalDeleteSign, setOpenModalDeleteSign] = useState(false);
  const [openModalManageServices, setOpenModalManageServices] = useState(false);
  const [openModalAssignServices, setOpenModalAssignServices] = useState(false);
  const [openModalSyncCentres, setOpenModalSyncCentres] = useState(false);
  const [sign, setSign] = useState<TSign>(initSign);
  const [selectedSignIds, setSelectedSignIds] = useState<number[]>([]);

  // Kept on the page, not in the modal: the report outlives the dialog so the
  // operator can still read which centres failed after closing it.
  const [syncReport, setSyncReport] = useState<SyncCentresResult | null>(null);

  // API calls - Include etablissement_id in the query parameters
  const { signs, pagination, isPending } = useGetAllSigns({
    page: currentPage,
    per_page: rowsPerPage,
    ...(searchTerm && { search: searchTerm }),
    ...(selectedEtablissementId && { etablissement_id: selectedEtablissementId }) // Add etablissement filter
  });

  // Fetch etablissements for dropdown
  const { etablissements } = useGetAllEtablissements({
    page: 1,
    per_page: 1000 // Get all etablissements for dropdown
  });

  const assignServicesToSignsMutation = useAssignServicesToSigns(() => {
    setOpenModalAssignServices(false);
    setSelectedSignIds([]);
  });

  const onhandleClose = () => {
    setOpenModalSign(false);
    setOpenModalDeleteSign(false);
    setOpenModalManageServices(false);
    setOpenModalAssignServices(false);
    setSign(initSign);
    setSelectedSignIds([]);
  };

  const handleCloseSyncCentres = useCallback(() => {
    setOpenModalSyncCentres(false);
  }, []);

  const handleOpenSyncCentres = useCallback(() => {
    // A previous report would sit above a fresh run and read as its result.
    setSyncReport(null);
    setOpenModalSyncCentres(true);
  }, []);

  const handleEditSign = (sign: TSign) => {
    const normalizedSign: TSign = {
      ...sign,
      address: sign.address || {
        raw: "",
        lat: 0,
        lng: 0,
        city: null,
        country: null
      },
      services: sign.services || [],
      etablissement_id: sign.etablissement_id || null,
      etablissement: sign.etablissement || null
    };
    
    setOpenModalSign(true);
    setSign(normalizedSign);
  };

  const handleDeleteSign = (sign: TSign) => {
    setOpenModalDeleteSign(true);
    setSign(sign);
  };

  const handleManageServices = (sign: TSign) => {
    setOpenModalManageServices(true);
    setSign(sign);
  };

  const handleAssignServices = () => {
    if (selectedSignIds.length > 0) {
      setOpenModalAssignServices(true);
    }
  };

  const handleAssignServicesSubmit = (serviceIds: number[]) => {
    assignServicesToSignsMutation.mutate({
      sign_ids: selectedSignIds,
      service_ids: serviceIds
    });
  };

  const handleSelectRow = useCallback((selectedRows: Record<string, any>[]) => {
    const selectedIds = selectedRows.map(row => row.id);
    setSelectedSignIds(selectedIds);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage + 1);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  }, []);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500);
  }, []);

  // New handler for etablissement filter
  const handleEtablissementChange = useCallback((event: SelectChangeEvent<number | "">) => {
    const value = event.target.value;
    // Convert string to number if it's not empty, otherwise keep as empty string
    const convertedValue = value === "" ? "" : Number(value);
    setSelectedEtablissementId(convertedValue);
    setCurrentPage(1); // Reset to first page when filter changes
  }, []);

  // Clear etablissement filter
  const handleClearEtablissementFilter = useCallback(() => {
    setSelectedEtablissementId("");
    setCurrentPage(1);
  }, []);

  const actionsColumns = [
    {
      header: "",
      field: "",
      render: (data: Record<string, any>) => {
        const signData: TSign = {
          id: data.id,
          name: data?.name || "",
          logo: data?.logo,
          phone: data?.phone,
          email: data?.email,
          created_at: data?.created_at,
          updated_at: data?.updated_at || "",
          picture_1: data?.picture_1,
          picture_2: data?.picture_2,
          picture_3: data?.picture_3,
          centre_code: data?.centre_code,
          postal_code: data?.postal_code,
          google_place_id: data?.google_place_id,
          working_day: data?.working_day,
          unworking_day: data?.unworking_day,
          distance: data?.distance || 0,
          like: data?.like || false,
          etablissement_id: data?.etablissement_id,
          etablissement: data?.etablissement,
          address: data?.address || {
            raw: "",
            lat: 0,
            lng: 0,
            city: null,
            country: null
          },
          services: data?.services || []
        };
        
        return (
          <ActionsTable
            handleEditSign={() => handleEditSign(signData)}
            handleDeleteSign={() => handleDeleteSign(signData)}
            handleManageServices={() => handleManageServices(signData)}
          />
        );
      },
    },
  ];

  const columnsWithActions = [...columns, ...actionsColumns];

  const BulkActionsHeader = () => (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      {selectedSignIds.length > 0 && (
        <CustomButtonTable
          variant="secondary"
          textVariant="link"
          label={t("sign.assignServicesTo", "Assign Services to {{count}} Sign(s)", { count: selectedSignIds.length })}
          width="fit-content"
          fontWeight="bold"
          textDecoration="none"
          iconName="settings"
          padding="0px 15px"
          textTransform="uppercase"
          onClick={handleAssignServices}
        />
      )}
    </div>
  );

  // Filter Controls Component
  const FilterControls = () => (  
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      alignItems: 'center', 
      marginBottom: 2,
      flexWrap: 'wrap'
    }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="etablissement-filter-label">
          {t("sign.filterByEtablissement", "Filter by Etablissement")}
        </InputLabel>
        <Select
          labelId="etablissement-filter-label"
          id="etablissement-filter"
          value={selectedEtablissementId}
          label={t("sign.filterByEtablissement", "Filter by Etablissement")}
          onChange={handleEtablissementChange}
          displayEmpty={false} // Remove displayEmpty to avoid conflicts
        >
          {/* Remove the placeholder MenuItem with empty value */}
          <MenuItem value="">
            <em>{t("sign.allEtablissements", "All Etablissements")}</em>
          </MenuItem>
          {etablissements?.map((etablissement: any) => (
            <MenuItem key={etablissement.id} value={etablissement.id}>
              {etablissement.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedEtablissementId && (
        <CustomButtonTable
          variant="secondary"
          textVariant="link"
          label={t("common.clear", "Clear")}
          width="fit-content"
          fontWeight="normal"
          textDecoration="none"
          iconName="x"
          padding="8px 12px"
          textTransform="none"
          onClick={handleClearEtablissementFilter}
        />
      )}

      {/* Show active filter info */}
      {selectedEtablissementId && (
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          padding: '4px 8px', 
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: '#666'
        }}>
          {t("sign.filteringBy", "Filtering by")}: {
            etablissements?.find((e: any) => e.id === selectedEtablissementId)?.name
          }
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {(isPending || assignServicesToSignsMutation.isPending) && (
        <CustomLoader isActive={isPending || assignServicesToSignsMutation.isPending} />
      )}

      <ModalAddSign
        open={openModalSign}
        signInitial={sign}
        etablissements={etablissements}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      
      <ModalDeleteSign
        open={openModalDeleteSign}
        signInitial={sign}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />

      <ModalManageServices
        open={openModalManageServices}
        signId={sign.id}
        services={sign.services || []}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />

      <ModalAssignServices
        open={openModalAssignServices}
        selectedSignIds={selectedSignIds}
        onAssign={handleAssignServicesSubmit}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />

      <ModalSyncCentres
        open={openModalSyncCentres}
        onClose={handleCloseSyncCentres}
        onResult={setSyncReport}
      />

      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.containerHeader}>
          <div className={classes.divIconArrow}>
            <CustomTypography
              variant="h2"
              text={t("sign.signManagement", "Sign Management")}
              isUppercase
            />
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/*
              Secondary to "Add Sign" on purpose: this is a bulk network
              operation, not the routine action on this screen.
            */}
            <CustomButtonTable
              variant="default"
              textVariant="link"
              label={t("centres.syncCentres", "Synchroniser les centres")}
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="reload"
              padding="0px 15px"
              textTransform="uppercase"
              onClick={handleOpenSyncCentres}
            />

            <CustomButtonTable
              variant="secondary"
              textVariant="link"
              label={t("sign.addSign", "Add Sign")}
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="plus"
              padding="0px 15px"
              textTransform="uppercase"
              onClick={() => setOpenModalSign(true)}
            />
          </div>
        </div>

        {syncReport && (
          <SyncCentresReport
            result={syncReport}
            onDismiss={() => setSyncReport(null)}
          />
        )}

        {/* Add Filter Controls */}
        <FilterControls />

        <CustomTableSign
          columns={columnsWithActions}
          data={signs || []}
          isSearchable
          showSelectedField
          tableMaxHeight="520px"
          titleHeader={t("sign.listOfSigns", "List of Signs")}
          isFilterFromBackend={true}
          showPagination={true}
          tablePagnationCount={pagination?.total || 0}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          handleChangePageProps={handlePageChange}
          onChangeSearchTerm={handleSearchChange}
          currentPage={currentPage - 1}
          currentRowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSelectRow={handleSelectRow}
          componentHeader={<BulkActionsHeader />}
        />
      </Sidebar>
    </>
  );
};